<template>
  <header
    :class="[headerClasses]"
    class="popup-right-0 sticky left-0 right-0 top-0 bg-white md:z-[2] print:hidden"
    :style="{ transform: `translateY(${translateY}px)` }"
  >
    <TopAlerts />

    <JContainer class="py-12" wide>
      <nav
        ref="headerNavRef"
        class="mx-auto flex items-center justify-between"
        aria-label="Global"
      >
        <div class="flex lg:flex-1">
          <JLogo :title="brandTitle" src="/logo/logo.svg" />
        </div>

        <div v-if="isDesktop" class="flex">
          <div class="flex items-center">
            <ul class="flex items-center gap-x-24">
              <li v-for="item in localizedHeaderItems" :key="item.title">
                <button
                  v-if="
                    (item.noAuth || isAuthenticated) && item.event !== 'help'
                  "
                  class="text-subject font-medium"
                  @click="onClickItem(item.event, item)"
                >
                  {{ item.title }}
                </button>
              </li>
            </ul>
          </div>

          <div class="flex items-center gap-x-16">
            <button @click="emitContactUs">
              <JIcon
                v-if="featureFlag.ui.headerContactUsType === 'icon'"
                name="headphones"
                size="20"
              />
              <span v-else class="text-subject font-medium">
                {{ $t('support.contactUs') }}
              </span>
            </button>

            <ul class="flex items-center">
              <li v-for="item in localizedHeaderItems" :key="item.title">
                <button
                  v-if="item.event === 'help'"
                  class="text-subject font-medium"
                  @click="helpStore.toggleHelpModal(true)"
                >
                  {{ item.title }}
                </button>
              </li>
            </ul>

            <HeaderContentCurrencyMenu v-if="showCurrency" />

            <HeaderContentLanguageMenu v-if="showMultiLang" />

            <div
              class="flex items-center gap-16"
              :class="isAuthenticated ? 'flex-row-reverse' : 'flex-row'"
            >
              <AffiliateButton
                v-if="featureFlag.affiliateProgram"
                @confirm="$emit('affiliate')"
              />

              <JButton
                v-if="!isAuthenticated && !isLoading"
                color="secondary"
                variant="tonal"
                class="flex flex-row items-center gap-4"
                @click="emitShowProfile"
              >
                <JIcon name="user-octagon-alt" size="20" />
                <span class="font-medium">
                  {{ $t('search.loginRegister') }}
                </span>
              </JButton>

              <HeaderContentProfile
                v-if="showProfile && isAuthenticated"
                :isOpen="showProfile"
                @logout="$emit('logout')"
              />
            </div>

            <ItinerarySummaryToggler
              v-if="
                $route.name === 'Search' ||
                $route.name?.toString().includes('CuratedTravelJourneys')
              "
            >
              <JButton
                :color="itineraryLength ? 'primary' : 'secondary'"
                class="relative border-transparent"
                :disabled="!itineraryLength"
                @click="$emit('showItinerary')"
              >
                <span class="font-medium">
                  {{ $t('search.itinerarySummary.title') }}
                </span>

                <JIcon name="arrow-right-outline" />

                <span
                  v-if="itineraryLength"
                  class="absolute -right-8 -top-8 flex h-16 min-w-16 items-center justify-center rounded-full bg-danger text-metadata font-medium"
                >
                  {{ itineraryLength }}
                </span>
              </JButton>
            </ItinerarySummaryToggler>
          </div>
        </div>
      </nav>
    </JContainer>

    <Transition
      enterFromClass="opacity-0 -translate-y-4"
      enterActiveClass="transition duration-20"
      enterToClass="opacity-100 translate-y-0"
      leaveFromClass="opacity-100 translate-y-0"
      leaveActiveClass="transition duration-0"
      leaveToClass="opacity-0 -translate-y-4"
    >
      <HeaderContentSearchHistory
        v-if="isDisplaySearchContent && searchStore.isSearched && isFixed"
      />
    </Transition>
  </header>
</template>

<script lang="ts" setup>
import { useWindowScroll } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { JButton, JContainer, JIcon, JLogo } from '@journey/ui-kit'
import type { Props } from '../TheHeader.types'
import type { Emits, Events, HeaderItem } from './TheHeaderContent.types'
import {
  HEADER_ITEMS,
  HEADER_OFFSET_SCROLL,
} from './TheHeaderContent.constants'
import HeaderContentSearchHistory from './HeaderContentSearchHistory/HeaderContentSearchHistory.vue'
import HeaderContentCurrencyMenu from './HeaderContentCurrencyMenu/HeaderContentCurrencyMenu.vue'
import HeaderContentLanguageMenu from './HeaderContentLanguageMenu/HeaderContentLanguageMenu.vue'
import HeaderContentProfile from './HeaderContentProfile/HeaderContentProfile.vue'
import useHelpStore from '@/modules/Help/store'
import useAuthStore from '@/modules/Auth/store'
import useDeviceType from '@/modules/Core/composables/useDevicePlatform'
import useItinerarySummary from '@/modules/Search/store/itinerarySummary'
import useSearchStore from '@/modules/Search/store'
import TopAlerts from '@/modules/Core/components/partials/TopAlerts/TopAlerts.vue'
import ItinerarySummaryToggler from '@/modules/Search/components/ItinerarySummary/ItinerarySummaryToggler/ItinerarySummaryToggler.vue'
import useAuth from '@/modules/Auth/composables/useAuth'
import featureFlag from '@/config/featureFlag'
import AffiliateButton from '@/modules/Affiliate/components/AffiliateButton/AffiliateButton.vue'

withDefaults(defineProps<Props>(), {
  showProfile: true,
  showMultiLang: true,
  showCurrency: true,
})

const emit = defineEmits<Emits>()

const router = useRouter()
const route = useRoute()
const { isDesktop } = useDeviceType()

const searchStore = useSearchStore()
const itinerarySummaryStore = useItinerarySummary()

const itineraryLength = computed(
  () => Object.values(itinerarySummaryStore.itineraries || {}).flat().length
)

// HEADER ITEMS
const { t } = useI18n()

const authStore = useAuthStore()
const { isAuthenticated: isAuth0Authenticated, isLoading } = useAuth()

const isAuthenticated = computed(() => {
  if (featureFlag.authMode === 'legacy') {
    return authStore.loggedIn
  }
  return isAuth0Authenticated.value
})

const localizedHeaderItems = computed(() =>
  HEADER_ITEMS.filter((item) => {
    return !(
      item.event === 'loyaltyAccount' && !featureFlag.loyaltyAccount.show
    )
  }).map((item) => ({
    ...item,
    title: t(item.title),
  }))
)

function onClickItem(eventName: Events, item: HeaderItem) {
  if (item.route) {
    router.push(item.route)
  } else {
    emit(eventName, item)
  }
}

// PROFILE
function emitShowProfile() {
  emit('showProfile')
}

// RECENT ACTIVITY
const helpStore = useHelpStore()

// Contact Us
function emitContactUs() {
  emit('contactUs')
}

const { y } = useWindowScroll()

// const headerRef = ref<HTMLElement>()
const headerNavRef = ref<HTMLElement>()
const translateY = ref(0)
const isFixed = ref(false)

const headerClasses = computed(() =>
  isFixed.value
    ? 'transform transition-transform duration-300 ease-in-out shadow'
    : 'backdrop-transparent'
)

function getHeaderHeight() {
  return Number(headerNavRef.value?.offsetHeight) + HEADER_OFFSET_SCROLL || 0
}

function onScroll() {
  const headerHeight = getHeaderHeight()
  const scrollTop = y.value

  if (scrollTop >= headerHeight) {
    setFixedHeader()
    return
  }

  setScrollingHeader(scrollTop)
}

function setFixedHeader() {
  isFixed.value = true
  translateY.value = 0
}

function setScrollingHeader(scrollTop: number) {
  isFixed.value = false
  translateY.value = -scrollTop
}

const isDisplaySearchContent = computed(() => route.name === 'Search')

onMounted(() => window.addEventListener('scroll', onScroll))

onUnmounted(() => window.removeEventListener('scroll', onScroll))
</script>
